import React, { useEffect, useMemo, useRef, useState } from "react";

import type { ColDef, GetRowIdParams, GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import {
  Alert,
  Button,
  Divider,
  Group,
  Box,
  Flex,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch as MantineSwitch,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IOtherAttributeConfigApi,
  IOtherAttributesFormula,
  IOtherAttributesFormulaConfItem,
} from "@mcapps/shared/schemas/otherAttributesSchema";
import { useImmer } from "use-immer";
import { HSpace, McTitle, VSpace } from "../../index";
import "./styles.css";

const rowClassRules = {
  "green-row": 'data.type == "DELIMITER"',
  "blue-row": 'data.type == "ATTRIBUTE"',
  "counter-row": 'data.type == "COUNTER"',
};

const defaultColDef: ColDef = {
  flex: 1,
  minWidth: 100,
  filter: true,
};

export interface FormulaAttributeOption {
  value: string;
  label: string;
}
const EMPTY_BASE_ATTRIBUTES: FormulaAttributeOption[] = [];

const FormulaConfig: React.FC<{
  formula: IOtherAttributesFormula;
  otherAttributesConfig: IOtherAttributeConfigApi;
  baseAttributes?: FormulaAttributeOption[];
  onFormulaConfUpdate: (formula: IOtherAttributesFormula) => void;
}> = ({ formula, onFormulaConfUpdate, otherAttributesConfig, baseAttributes = EMPTY_BASE_ATTRIBUTES }) => {
  const [leftApi, setLeftApi] = useState<GridApi | null>(null);
  const [rightApi, setRightApi] = useState<GridApi | null>(null);
  const [rightRowData, setRightRowData] = useImmer<IOtherAttributesFormulaConfItem[]>([]);

  const leftColumns: ColDef<IOtherAttributesFormulaConfItem>[] = [
    { field: "id", hide: true },
    {
      rowDrag: true,
      field: "value",
      headerName: "Attribut",
      width: 200,
      cellRenderer: (params) => {
        return getAttributeLabel(params.value);
      },
    },
  ];

  const RightColumns: ColDef<IOtherAttributesFormulaConfItem>[] = [
    { field: "id", hide: true },
    { field: "type", hide: true },
    {
      rowDrag: true,
      field: "value",
      filter: false,
      headerName: "Attribut",
      cellRenderer: (params) => {
        const rowId = params.data.id;
        const label =
          params.data.type == "COUNTER"
            ? "Löpnummer"
            : params.data.type === "DELIMITER"
            ? params.value
            : getAttributeLabel(params.value);
        return (
          <span>
            <i
              style={{ cursor: "pointer" }}
              onClick={() =>
                onFormulaConfUpdate({
                  ...formula,
                  confArr: rightRowData.filter((item) => item.id !== rowId),
                })
              }
              className="fa fa-trash fa-fw"
            />
            {label}
          </span>
        );
      },
    },
  ];

  const leftRowData = useMemo<IOtherAttributesFormulaConfItem[]>(() => [
    ...baseAttributes.map(({ value }) => ({ id: value, type: "ATTRIBUTE" as const, value })),
    ...Object.entries(otherAttributesConfig)
      .filter(([, attribute]) => attribute.typeOfValue !== "FORMULA")
      .map(([key]) => ({ id: key, type: "ATTRIBUTE" as const, value: key })),
  ], [otherAttributesConfig, baseAttributes]);

  useEffect(() => {
    let _formula: IOtherAttributesFormula = null;
    if (formula == null) {
      _formula = {
        nrOfCharsInCounter: 3,
        confArr: [],
      };
    } else {
      _formula = formula;
    }
    const rows = (_formula.confArr ?? []).map((item) => {
      return {
        id: item.id ?? Math.random().toString(36).substring(7),
        type: item.type,
        value: item.value,
      };
    });
    setRightRowData(rows);
  }, [formula]);

  const eLeftGrid = useRef(null);
  const eRightGrid = useRef(null);

  const getRowId = (params: GetRowIdParams) => String(params.data.id);

  const addGridDropZone = (side: string, api: GridApi) => {
    const dropApi = side === "Left" ? rightApi : leftApi;
    const dropZone = dropApi!.getRowDropZoneParams();

    api.addRowDropZone(dropZone);
  };

  useEffect(() => {
    if (rightApi && leftApi) {
      addGridDropZone("Right", rightApi);
      addGridDropZone("Left", leftApi);
    }
  });

  const onGridReady = (side: string, params: GridReadyEvent) => {
    if (side === "Left") {
      setLeftApi(params.api);
    } else {
      setRightApi(params.api);
    }
  };

  const onRowDragEnd = (grid: string, event: any) => {
    if (grid === "Left") {
      return;
    }
    const rows = event.api?.getRenderedNodes();
    onFormulaConfUpdate({
      ...formula,
      confArr: rows.map((row) => {
        return {
          id: row.data.id,
          type: row.data.type,
          value: row.data.value,
        };
      }),
    });
  };

  const getInnerGridCol = (side: string) => (
    <div className="inner-col">
      <div style={{ height: "100%" }} className="inner-col" ref={side === "Left" ? eLeftGrid : eRightGrid}>
        <AgGridReact
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          rowClassRules={rowClassRules}
          rowDragManaged={true}
          suppressMoveWhenRowDragging={true}
          readOnlyEdit={true}
          onRowDragEnd={(event) => onRowDragEnd(side, event)}
          rowData={side === "Left" ? leftRowData ?? [] : rightRowData ?? []}
          columnDefs={side === "Left" ? leftColumns : RightColumns}
          onGridReady={(params) => onGridReady(side, params)}
        />
      </div>
    </div>
  );

  const addOwnDelimiter = (str: string) => {
    onFormulaConfUpdate({
      ...formula,
      confArr: [
        ...formula.confArr,
        {
          id: Math.random().toString(36).substring(7),
          type: "DELIMITER",
          value: str,
        },
      ],
    });
    setInputValue("");
  };

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      addOwnDelimiter(inputValue);
      setInputValue("");
      return;
    }
  };

  const [inputValue, setInputValue] = useState("");

  const addCounter = () => {
    if (rightRowData.find((row) => row.type === "COUNTER")) {
      return;
    }
    onFormulaConfUpdate({
      ...formula,
      confArr: [
        ...formula.confArr,
        {
          id: Math.random().toString(36).substring(7),
          type: "COUNTER",
          value: "Counter",
        },
      ],
    });
  };

  const counterIsAdded = rightRowData.find((row) => row.type === "COUNTER") != null;

  const generatedSting = rightRowData
    .map((row) => {
      if (row.type === "ATTRIBUTE") {
        return baseAttributes.find((attribute) => attribute.value === row.value)?.label.toUpperCase() || row.value;
      }
      if (row.type === "COUNTER") {
        return getLabelForCounter(formula.nrOfCharsInCounter);
      }
      return row.value;
    })
    .join("");

  const getAttributeLabel = (value: string) => {
    const label = baseAttributes.find((attribute) => attribute.value === value)?.label;
    if (label) {
      return label;
    }
    const otherAttribute = otherAttributesConfig[value];
    if (otherAttribute) {
      return `(${value}) ${otherAttribute.label}`;
    }
  };

  const nrOfCharsOptions = [
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" },
  ];

  return (
    <>
      <VSpace />
      <Paper withBorder p="sm" radius="sm" bg="#f8f9fa" style={{ height: "100%", width: "100%" }}>
        <Stack gap="xs" style={{ height: "100%" }}>
          <Title order={5} fw={400}>
            Konfigurasjon for formell
          </Title>
          <div className="ag-theme-alpine" style={{ height: 500, width: "100%" }}>
            <Flex direction="column" h="100%">
              <VSpace />
              <Box className="bg-warning border-radius" p="xs">
                <VSpace />
                <h6>{generatedSting}</h6>
              </Box>
              <VSpace />
              <Flex h="100%" w="100%">
                {getInnerGridCol("Left")}
                <HSpace />
                <Flex direction="column" h="100%">
                  <Box>
                    <Text fw={600} mb={6}>
                      Skriv inn tegn
                    </Text>
                    <TextInput
                      autoFocus
                      value={inputValue}
                      placeholder="Legg til valg"
                      styles={{
                        input: {
                          backgroundColor: "#c7e8c5",
                          maxWidth: 220,
                        },
                      }}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={blurOnEnter}
                      onBlur={(e) => {
                        if (e.target.value.length > 0) {
                          setInputValue("");
                        }
                      }}
                    />
                  </Box>
                  <Divider label="Löpnummer" labelPosition="center" my="sm" />
                  <Button
                    color="orange"
                    variant="light"
                    disabled={counterIsAdded}
                    leftSection={<i className="fa fa-list-ol fa-fw" />}
                    onClick={() => addCounter()}
                  >
                    {counterIsAdded ? "Löpnummer lagt til" : "Legg til löpnummer"}
                  </Button>
                </Flex>
                <HSpace />
                {getInnerGridCol("Right")}
              </Flex>
            </Flex>
          </div>
          <hr />
          {counterIsAdded && (
            <Paper withBorder p="md" radius="sm" mb="md" bg="white" style={{ borderColor: "#f08c0066" }}>
              <Stack gap="sm">
                <Group gap="xs">
                  <ThemeIcon color="orange" variant="light" radius="sm">
                    <i className="fa fa-list-ol fa-fw" />
                  </ThemeIcon>
                  <Text fw={600}>Löpnummer</Text>
                </Group>
                <Box>
                  <Text size="sm" fw={600} mb={6}>
                    Antall tegn i löpnummer
                  </Text>
                  <Select
                    aria-label="Antall tegn i löpnummer"
                    value={String(formula.nrOfCharsInCounter ?? 3)}
                    onChange={(value) => {
                      const nrOfCharsInCounter = Number(value);
                      if (Number.isNaN(nrOfCharsInCounter)) {
                        return;
                      }

                      onFormulaConfUpdate({
                        ...formula,
                        nrOfCharsInCounter,
                      });
                    }}
                    data={nrOfCharsOptions.map((option) => ({
                      value: String(option.value),
                      label: option.label,
                    }))}
                  />
                  <Text size="sm" c="dimmed" mt={6}>
                    Bestemmer minimumslengden på nummeret. Eksempel: 3 tegn gir 001, 002 og 101. 4 tegn gir 0001 og
                    0002.
                  </Text>
                </Box>
                <SegmentedControl
                  color="orange"
                  value={formula.counterMode ?? "AUTO"}
                  onChange={(value) =>
                    onFormulaConfUpdate({
                      ...formula,
                      counterMode: value as IOtherAttributesFormula["counterMode"],
                      manualCounter: {
                        enforceUniqueWithinGroup: formula.manualCounter?.enforceUniqueWithinGroup ?? true,
                      },
                    })
                  }
                  data={[
                    { value: "AUTO", label: "Automatisk" },
                    { value: "MANUAL", label: "Manuell" },
                  ]}
                />
                {(formula.counterMode ?? "AUTO") === "AUTO" ? (
                  <>
                    <Alert color="blue">
                      Löpnummer beregnes automatisk i kronologisk ordning innen hver formelgruppe. Rekkefølgen følger
                      når postene ble opprettet.
                    </Alert>
                  </>
                ) : (
                  <>
                    <Alert color="yellow">
                      Brukeren angir løpnummeret selv. Systemet kan fortsatt bruke samme formel til å foreslå neste
                      ledige nummer.
                    </Alert>
                    <Group>
                      <MantineSwitch
                        checked={formula.manualCounter?.enforceUniqueWithinGroup !== false}
                        onChange={(event) =>
                          onFormulaConfUpdate({
                            ...formula,
                            manualCounter: {
                              enforceUniqueWithinGroup: event.currentTarget.checked,
                            },
                          })
                        }
                        label="Krev unikt nummer innen samme formelgruppe"
                      />
                    </Group>
                  </>
                )}
              </Stack>
            </Paper>
          )}
          <McTitle title="Fjern foregående tegnsetting hvis attributtet ikke er angitt">
            <MantineSwitch
              size="sm"
              checked={formula.deletePreviousDelimiterIfAttributeIsEmpty || false}
              onChange={(event) =>
                onFormulaConfUpdate({
                  ...formula,
                  deletePreviousDelimiterIfAttributeIsEmpty: event.currentTarget.checked,
                })
              }
            />{" "}
          </McTitle>
        </Stack>
      </Paper>
    </>
  );
};

export default FormulaConfig;

const getLabelForCounter = (nrOfChars: number): string => {
  // nr of chars = 3 => "001", nr of chars = 4 => "0001", nr of chars = 5 => "00001", etc
  if (nrOfChars === 3) {
    return "001";
  }
  if (nrOfChars === 4) {
    return "0001";
  }
  if (nrOfChars === 2) {
    return "01";
  }
};
